import { memo, useRef, useMemo, useState, useCallback } from 'react';
import { AppId, Dropdown, Portal, sanitize, useOutsideClick, usePlatformContext } from '@datassential/platform-ui-lib';
import { ByoTableRow } from "../byoTable.helpers";
import Icon from '@components/Icon/Icon';
import { IconMenu } from '@icons';
import { useFilters } from '@vms/SelectFilters/SelectFiltersContext';
import KeywordMenu, { KeywordMenuOption } from './KeywordMenu';

interface ByoKeywordCellProps {
    keyword: ByoTableRow;
    openMenuExamples: () => void;
}

const KeywordCellMenuDropdown = ({
    keyword,
    openMenuExamples,
}: ByoKeywordCellProps) => {
    const menuButtonRef = useRef<HTMLDivElement>(null);
    const { userInfo: { products } } = usePlatformContext();
    const { countryCode: currentCountry } = useFilters();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    
    const DROPDOWN_ID = "keyword-cell-dropdown" + '-' + keyword.name?.replaceAll(' ', '-');
    const isUsa = currentCountry === 'USA';

    const isLocalProductEnabled = useMemo(
        () => !products[AppId.LocalInsights]?.isDisabled,
        [products]
    );
    
    const localProductLink = useMemo(
        () => products[AppId.LocalInsights]?.url,
        [products]
    );
    
    const optionsKeywordMenu = useMemo(() => {
        const { isFoodProfile, id, name, isLocal } = keyword;
        const options: KeywordMenuOption[] = [];
        
        if (isFoodProfile && isUsa) {
            options.push({
                title: "View profile",
                url: `/food-profile?id=${id}&name=${encodeURIComponent(name ?? '')}`,
            });
        }

        options.push({
            title: "View menu examples",
            onClick: openMenuExamples,
        });

        if (isLocalProductEnabled && isLocal) {
            options.push({
                title: "View local insights",
                isExternal: true,
                url: sanitize(`${localProductLink}/${process.env.REACT_APP_LOCAL_KEYWORD_LINK}/${id}`),
            });
        }

        return options;
    }, [keyword, isUsa, isLocalProductEnabled, localProductLink, openMenuExamples]);

    const handleClose = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    useOutsideClick({
        ref: menuButtonRef,
        callback: handleClose,
        ignoreNodesIds: [DROPDOWN_ID],
    });

    return (
        <>
            <div ref={menuButtonRef}>
                <button
                    data-tooltip-id={DROPDOWN_ID}
                    className="KeywordCell__settings-btn"
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsMenuOpen((prev) => !prev);
                    }}
                >
                    <Icon type='small'>
                        <IconMenu />
                    </Icon>
                </button>
            </div>

            <Portal>
                <Dropdown
                    className="KeywordCell__menu"
                    id={DROPDOWN_ID}
                    isOpened={isMenuOpen}
                    place="bottom-start"
                    offset={4}
                    columns={[{
                        id: 'option',
                        items: [],
                        content: (
                            <KeywordMenu
                                options={optionsKeywordMenu}
                                onItemClick={handleClose}
                            />
                        ),
                    }]}
                />
            </Portal>
        </>
    );
};

export default memo(KeywordCellMenuDropdown);
